import { faSquareArrowDownRight } from "@fortawesome/pro-solid-svg-icons/faSquareArrowDownRight";
import { faSquareDashed } from "@fortawesome/pro-solid-svg-icons/faSquareDashed";
import { Avatar, Box, Grid, InputAdornment, Paper, SxProps, TextField, Theme, Tooltip, Typography, useTheme } from '@mui/material';
import Autocomplete, { AutocompleteChangeReason, AutocompleteProps } from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { GenericAbortSignal } from "axios";
import { MprcChip } from "components/MprcChip";
import { MprcIconFa } from 'components/MprcIconFa';
import { useMprcTheme } from 'components/MprcThemeProvider';
import { MprcTypography } from "components/MprcTypography";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import { Formaters } from "lib/Formaters";
import { assocRefToPropRef } from "lib/ref";
import { useSnackbar } from "notistack";
import { isCanceledError } from "providers/CoreApiProvider";
import React, { useEffect, useRef, useState } from 'react';
import { AutoCompleteHierarchicalModel, AutoCompleteHierarchicalSuggestions, AutoCompleteSelectedMode } from './AutoCompleteHierarchicalModel';

////////////////////////////////////////////////////////////////////////////
export type AutocompleteHierarchicalBaseApplyEnter = {
    onApply: () => void;
    behavior: "SelectionOptionsEmpty" | "anyway";
};

export interface IAutocompleteHierarchicalBaseProps {
    id?: string;
    label?: string | null;
    placeholder?: string;
    maxSuggestionCount?: number;
    maxSelectItems?: number;
    multiSelect?: boolean;
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    helperText?: React.ReactNode;
    autoFocus?: boolean;
    iconStart?: React.ReactNode;
    selectedMode: AutoCompleteSelectedMode;
    selectedItems: AutoCompleteHierarchicalModel[];
    showDescendantOption?: boolean;
    margin?: MuiMargin;
    applyEnter?: AutocompleteHierarchicalBaseApplyEnter;
    inputRef?: React.Ref<any>;
    color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
    grouperItemCannotBeSelectedMessage?: string;
    itemCannotBeSelectedMessage?: string;
    sx?: SxProps<Theme>;
    // Ao pressionar ESC quando nenhum item esta selecionado
    onCancel?: () => void;
}

export interface IAutocompleteHierarchicalProps extends IAutocompleteHierarchicalBaseProps {
    onGetSuggestions: (parentId: string | null, trySelectedHistory: boolean, searchText: string, pageNumber: number, pageSize: number, signal: GenericAbortSignal | undefined) => Promise<AutoCompleteHierarchicalSuggestions<AutoCompleteHierarchicalModel>>;
    onChange: (item: AutoCompleteHierarchicalModel[]) => void;
    onKeyReturn?: (value: string) => void;
    onSelectItem?: (item: AutoCompleteHierarchicalModel) => void;
    onDisplayLabel?: (option: AutoCompleteHierarchicalModel) => string;
    onDisplayTooltip?: (option: AutoCompleteHierarchicalModel) => string;
}

type Props<T> = IAutocompleteHierarchicalProps &
    Omit<AutocompleteProps<T, boolean, boolean, boolean>,
        "value" | "options" | "renderInput" | "onChange" | "loading" | "isOptionEqualToValue" | "renderTags" | "helperText" | "multiple" | "disabled" | "startAdornment" | "placeholder">;

const KEY_MORE_ITEMS = "key_more_items";
const KEY_INCLUDE_DESCENDANTS = "key_include_descendants";
const KEY_INCLUDE_THIS_ONLY = "key_include_this_only";
const SEARCH_DELAY = 300;

////////////////////////////////////////////////////////////////////////////

/**
 * Componente autocomplete baseado no MUI
 * @param props
 */
export const MprcAutoCompleteHierarchical: React.FC<Props<AutoCompleteHierarchicalModel>> = (props) => {
    const {
        id = new Date().getTime().toString(),
        label, placeholder, iconStart, error, helperText, autoFocus,
        color, sx,
        inputRef,
        selectedMode,
        selectedItems,
        maxSuggestionCount = 10,
        multiSelect = true,
        required = false,
        disabled = false,
        maxSelectItems,
        margin,
        showDescendantOption = false,
        grouperItemCannotBeSelectedMessage,
        itemCannotBeSelectedMessage,
        applyEnter,
        onGetSuggestions, onDisplayLabel, onDisplayTooltip, onChange, onSelectItem, onCancel
    } = props;
    const { enqueueSnackbar } = useSnackbar();
    const { theme } = useMprcTheme();
    const muiTheme = useTheme();

    const [loading, setLoading] = useState<boolean>(false);
    const [refresh, setRefresh] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const [suggestions, setSuggestions] = useState<AutoCompleteHierarchicalModel[]>([]);
    const [parentTyped, setParentTyped] = useState<AutoCompleteHierarchicalModel | null>(null);
    const [inputValue, setInputValue] = useState<string>("");
    const [focused, setFocused] = useState<boolean>(false);

    // Registra o item selecionado na lista pelo mouse ou teclado
    const optionHighlight = useRef<any>(undefined);

    const isMoreItems = useRef<boolean>(false);
    const lastItemSelected = useRef<AutoCompleteHierarchicalModel | null>(null);
    const abortControllerRef = useRef<AbortController>(undefined);
    const alreadyChecked = useRef<boolean>(false);
    const localInputRef = React.useRef<HTMLInputElement | null>(null);

    /**
     * Define se está no primeiro nível e é para tentar verificar se tem histórico de seleção.
     * @deprecated Recurso desabilitado
     */
    const trySelectionHistory = useRef<boolean>(false);

    // Controla sequência de Enter pressionados
    const pressEnter = useRef<number>(0);

    const currentPath = parentTyped == null
        ? ""
        : parentTyped.tree && parentTyped.tree.length > 0 && parentTyped.tree[0] != null
            ? `${parentTyped.tree.map(x => Formaters.truncateString(x.parentLabel, 100)).join('\\')}\\${Formaters.truncateString(parentTyped.label, 100)}\\`
            : `${Formaters.truncateString(parentTyped.label, 100)}\\`;

    // Limpa o highlight do item selecionado
    function clearOptionHighlight() {
        optionHighlight.current = null;
    }

    ////////////////////////////////////////////////////////////////////////////
    /// HANDLES E FUNCTIONS
    ////////////////////////////////////////////////////////////////////////////

    // Seleciona um item pelas teclas de acesso
    function selectItem(item: AutoCompleteHierarchicalModel, includeDescendants: boolean): boolean {
        alreadyChecked.current = true;

        // Não permitir seleção de itens agrupadores para atribuição
        if (selectedMode == 'assignment' && !item.allowAssociates) {
            enqueueSnackbar(grouperItemCannotBeSelectedMessage ?? theme?.MprcAutoCompleteHierarchical?.grouperItemCannotSelected, { variant: "error" });
            clearOptionHighlight();
            return false;
        }
        else if (!item.selectable) {
            enqueueSnackbar(itemCannotBeSelectedMessage ?? theme?.MprcAutoCompleteHierarchical?.itemCannotSelected, { variant: "error" });
            clearOptionHighlight();
            return false;
        }

        // Define a árvore que será apresentada no Tooltip
        item.tooltip = onDisplayTooltip
            ? onDisplayTooltip(item)
            : item.tree && item.tree.length > 0 ? `${item.tree.map(x => x.parentLabel).join("\\")}\\${item.label}` : item.label;

        // Seleciona o item, caso já não esteja selecionado
        const selectedItem = { ...item, includeDescendants };
        if (multiSelect) {
            if (selectedItems.some(x => x.key == item.key)) {
                enqueueSnackbar(theme?.MprcAutoCompleteHierarchical?.itemAlreadySelected, { variant: "error" });
                return false;
            } else {
                onChange([...selectedItems, selectedItem]);
                if (onSelectItem) onSelectItem(selectedItem);
                trySelectionHistory.current = false;
                setOpen(false);
            }
        } else {
            onChange([selectedItem]);
            setOpen(false);
        }

        // Atualizar input para próxima edição
        setParentTyped(null);

        if (inputValue != "") {
            setInputValue("");
        }

        // Zera o contador de enters
        pressEnter.current = 0;

        return true;
    }

    // Seleciona um item para mostar startAdornment para continuar seleção da árvore
    function selectParentTyped(item: AutoCompleteHierarchicalModel | null) {
        setParentTyped(item);
        if (inputValue == "")
            setRefresh(!refresh);
        else
            setInputValue("");
    }

    // Se tem filhos e não permite associações, mostra o item no startAdornment para continuar seleção da árvore
    // Caso Contrário, seleciona o item
    function tryOrSelectItem(selected: AutoCompleteHierarchicalModel) {
        if (!selected.allowAssociates || selected.hasChild) {
            selectParentTyped(selected);
            pressEnter.current++;
            setTimeout(() => setOpen(true), SEARCH_DELAY);
        } else {
            selectItem(selected, false);
        }
    }

    // Obtem o primeiro item para seleção ao pressionar [/] ou [Enter]
    function getFirstSuggestion(): AutoCompleteHierarchicalModel | undefined {
        const index = suggestions.findIndex(p => p.key != KEY_INCLUDE_DESCENDANTS && p.key != KEY_INCLUDE_THIS_ONLY);
        if (index > -1) return suggestions[index];
        return undefined;
    }

    // Faz tratamentos para seleção para teclas pressionadas -> [\], [Enter] ou [Backspace]
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {

        // Se foi pressionado a barra [\]
        if (event.key == '\\' && suggestions.length > 0) {
            event.preventDefault();

            const selected = getFirstSuggestion();
            if (selected == undefined) return;

            // Se a lista é de histórico, limpa e carrega os dados da raiz
            if (selected.fromHistory) {
                trySelectionHistory.current = false;
                selectParentTyped(null);
            }
            // Não fazer nada se estiver carregando ou vázio
            else if (loading || inputValue.length == 0) {
                event.cancelable = false;
            }
            // Se for o último item da árvore
            else if (!selected.hasChild) {
                // Seleciona o item, se não for permitido selecionar foi enviado alerta.
                selectItem(selected, false);
            }
            // Mostra o item no startAdornment para continuar seleção da árvore
            else {
                selectParentTyped(selected);
            }
        }

        // Se foi pressionado a [BACKSPACE] e tem items para retornar edição na árvore.
        else if (event.key == 'Backspace' && inputValue.length == 0 && parentTyped) {
            event.preventDefault();

            setInputValue(parentTyped.label);
            clearOptionHighlight();

            if (parentTyped.tree && parentTyped.tree.length > 0 && parentTyped.tree[0] != null) {
                const lastItem = parentTyped.tree[parentTyped.tree.length - 1];

                const newParentTyped = {
                    key: lastItem.parentKey,
                    label: lastItem.parentLabel,
                    hasChild: lastItem.parentHasChild,
                    allowAssociates: lastItem.parentAllowAssociates,
                    tree: parentTyped.tree.slice(0, -1),
                    tooltip: parentTyped?.tree?.map(x => x.parentLabel).join("\\"),
                    fromHistory: false
                };
                setParentTyped(newParentTyped);
            }
            else {
                setParentTyped(null);
            }
        }

        // Se for pressionado o backspace com conteúdo vazio, remove o último item selecionado
        else if (event.key == 'Backspace' && inputValue.length == 0 && parentTyped == null && selectedItems.length > 0 && selectedItems[selectedItems.length - 1].canDelete) {
            event.preventDefault();

            if (!loading) {
                onChange(selectedItems.slice(0, -1));
            }
        }

        // Se foi pressionado o [ENTER]
        else if (event.key == 'Enter') {
            event.preventDefault();

            if (!loading) {

                if (multiSelect && !!maxSelectItems && maxSelectItems > 0 && !!selectedItems && selectedItems.length == maxSelectItems && event.keyCode != 8) {
                    return;
                }

                // Se for pressionado o [Enter] mais de uma vez, tenta selecionar o item se existir na árvore.
                if (parentTyped != null && pressEnter.current > 0 && inputValue == "") {
                    selectItem(parentTyped, !parentTyped.allowAssociates);
                    event.stopPropagation();
                }

                // Se for a opção para selecionar apenas este.
                else if (suggestions.length > 0 && parentTyped && optionHighlight.current?.key == KEY_INCLUDE_THIS_ONLY) {
                    selectItem(parentTyped, false);
                }
                // Se for a opção para selecionar todos (descendentes), selecionar com descendentes.
                else if (suggestions.length > 0 && parentTyped && optionHighlight.current?.key == KEY_INCLUDE_DESCENDANTS) {
                    selectItem(parentTyped, true);
                }
                else {
                    pressEnter.current++;
                }

                // Ao pressionar ENTER pela segunda vez
                if (applyEnter && parentTyped == null && inputValue == "" && selectedItems.length > 0 && pressEnter.current > 0 && currentPath.length == 0) {
                    if (applyEnter.behavior == "anyway" || suggestions.length == 0) applyEnter.onApply();
                }
            }
        }
        //Ao pressionar ESC
        else if (event.key == 'Escape') {
            if (onCancel && inputValue == "" && selectedItems.length == 0 && suggestions.length == 0) onCancel();
        }

        // Zera o contador de enter pressionados
        if (event.key != 'Enter') pressEnter.current = 0;

        trySelectionHistory.current = false;
        isMoreItems.current = false;
    };

    // Verifica se pode mudar as propriedades e chama o onChange
    const handleChange = (e: React.SyntheticEvent, value: AutoCompleteHierarchicalModel[], reason: AutocompleteChangeReason) => {


        // Quando um item é selecionado mas já existe, o componente aciona o evento como remoção.
        const isRemoveExistItem = reason == "removeOption" && selectedItems.some(x => x.key == optionHighlight.current?.key);
        if (isRemoveExistItem) {
            e.cancelable = true;
        }

        // Seleciona o item, se for remoção utiliza o item selecionado na lista para adicionar
        const lastItem = isRemoveExistItem && optionHighlight.current
            ? optionHighlight.current
            : value && value.length > 0
                ? value[value.length - 1]
                : null;

        lastItemSelected.current = lastItem ? lastItem : null;

        // Caso seja para carregar mais itens, aciona effect
        if (lastItemSelected.current?.key == KEY_MORE_ITEMS) {
            e.preventDefault();
            isMoreItems.current = true;
            doSearch();
            return;
        }

        // Tratamento para seleção na lista
        if ((reason == "selectOption" || isRemoveExistItem) && lastItem) {
            if (lastItem.key == KEY_INCLUDE_THIS_ONLY) {
                if (parentTyped) selectItem(parentTyped, false);
            }
            else if (lastItem.key == KEY_INCLUDE_DESCENDANTS) {
                if (parentTyped) selectItem(parentTyped, true);
            }
            else if (lastItem.fromHistory) {
                selectItem(lastItem, lastItem.includeDescendants);
            }
            else {
                tryOrSelectItem(lastItem);
            }
            return;
        }

        // if (reason == "removeOption" && isRemoveExistItem) {
        //     enqueueSnackbar(theme?.MprcAutoCompleteHierarchical?.itemAlreadySelected, { variant: "error" });
        // }
    };

    // Exclui o item pelo icone excluir do Chip
    const handleDeleteChip = (item: AutoCompleteHierarchicalModel | null) => {
        if (selectedItems && item) {
            const value = selectedItems.filter(x => x != item);
            onChange(value);
        }
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////

    function doSearch() {
        const offset = isMoreItems.current ? suggestions.length : 0;
        const pageNumber = Math.trunc(offset / maxSuggestionCount!) + 1;

        setLoading(true);
        abortControllerRef.current = new AbortController();

        onGetSuggestions(parentTyped?.key, false, inputValue.trim(), pageNumber, maxSuggestionCount!, abortControllerRef.current.signal)
            .then((data) => {
                let totalRows = data.items.length;
                let newSuggestions = data.items;

                // Incrementa o array em outro e remove a opção de mais itens
                if (isMoreItems.current) {
                    totalRows += suggestions.length;
                    newSuggestions = [...suggestions.filter(x => x.key != KEY_MORE_ITEMS), ...newSuggestions];
                }

                // Adiciona opção de todos abaixo para seleção dos descendentes
                if (showDescendantOption && pageNumber == 1 && parentTyped != null && inputValue.length == 0 && parentTyped.hasChild && selectedMode == "free") {
                    newSuggestions.unshift({
                        key: KEY_INCLUDE_DESCENDANTS,
                        label: theme?.MprcAutoCompleteHierarchical?.includeDescendants,
                        canDelete: false,
                        hasChild: false,
                        allowAssociates: false,
                        tree: [],
                        tooltip: "",
                        fromHistory: false,
                        includeDescendants: false
                    });

                    if (parentTyped.allowAssociates) {
                        newSuggestions.unshift({
                            key: KEY_INCLUDE_THIS_ONLY,
                            label: theme?.MprcAutoCompleteHierarchical?.selectSingle,
                            canDelete: false,
                            hasChild: false,
                            allowAssociates: false,
                            tree: [],
                            tooltip: "",
                            fromHistory: false,
                            includeDescendants: false
                        });
                    }
                }

                // Inclui a opção de mais itens, se tiver mais linhas para carregar
                if (data.totalRows > totalRows && maxSuggestionCount && maxSuggestionCount > 0) {
                    newSuggestions.push({
                        key: KEY_MORE_ITEMS,
                        label: theme.MprcAutoCompleteHierarchical.loadMoreText + ` ${maxSuggestionCount}`,
                        canDelete: false,
                        hasChild: false,
                        allowAssociates: false,
                        tree: [],
                        tooltip: "",
                        fromHistory: false
                    });
                }
                setSuggestions(newSuggestions);
            })
            .catch((e) => {
                if (!isCanceledError(e)) {
                    setSuggestions([]);
                }
            })
            .finally(() => {
                setLoading(false);
                abortControllerRef.current = undefined;
            });
    }

    // Executa a pesquisa depois de 600ms.
    useDidUpdateEffect(() => {
        const delayDebounce = setTimeout(() => {
            if (inputValue.slice(-1) != "\\") {
                abortControllerRef.current?.abort();
                doSearch();
            }
        }, SEARCH_DELAY);

        return () => {
            clearTimeout(delayDebounce);
            abortControllerRef.current?.abort();
        };
    }, [inputValue, refresh]);

    // Atualiza quando a caixa de items é aberta ou fechada.
    useEffect(() => {
        if (open) doSearch();
        else {
            setSuggestions([]);
            isMoreItems.current = false;
            lastItemSelected.current = null;
        }
    }, [open]);

    useEffect(() => {
        if (autoFocus && localInputRef) {
            localInputRef.current?.focus();
        }
    }, [localInputRef]);


    const handleInputRef = (ref: HTMLInputElement | null): void => {
        localInputRef.current = ref;

        if (inputRef) {
            assocRefToPropRef(ref, localInputRef);
        }
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <Autocomplete
            id={id}
            fullWidth
            multiple
            disabled={disabled}
            open={open}
            clearOnEscape
            disableClearable
            freeSolo={false}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={(e, reason) => {
                if (reason == "removeOption") {
                    if (!alreadyChecked.current && !optionHighlight.current?.hasChild)
                        enqueueSnackbar(theme?.MprcAutoCompleteHierarchical?.itemAlreadySelected, { variant: "error" });
                }
                else if (lastItemSelected.current?.key != KEY_MORE_ITEMS) {
                    setOpen(false);
                }

                if (reason == "escape") setParentTyped(null);
                lastItemSelected.current = null;
                alreadyChecked.current = false;
                clearOptionHighlight();
            }}
            loadingText={theme.MprcAutoCompleteHierarchical.loadingText}
            options={suggestions}
            loading={loading}
            getOptionLabel={(option) => typeof option === "string" ? option : option.label}
            inputValue={inputValue}
            value={selectedItems}
            onChange={handleChange}
            isOptionEqualToValue={(option, value) => option && value && option.key === value.key}
            filterSelectedOptions={false}
            filterOptions={(options) => options}
            noOptionsText={theme.MprcAutoComplete2.noOptionsText}
            renderInput={
                (params) => (
                    <TextField
                        autoFocus={autoFocus}
                        error={error}
                        helperText={helperText}
                        {...params}
                        required={required}
                        fullWidth
                        variant="standard"
                        margin={margin}
                        label={label}
                        inputRef={handleInputRef}
                        color={color}
                        sx={sx}
                        placeholder={
                            selectedItems && selectedItems.length > 0
                                ? undefined
                                : focused || open
                                    ? theme.MprcAutoCompleteHierarchical.inputPlaceholder
                                    : placeholder
                        }
                        onChange={(e) => {
                            if (disabled) return;
                            setInputValue(e.target.value);
                        }}
                        onKeyDown={handleKeyDown}
                        onFocus={() => {
                            if (multiSelect || (selectedItems && selectedItems.length == 0)) {
                                setOpen(true);
                            }
                            setFocused(true);
                        }}
                        onBlur={() => {
                            if (suggestions && suggestions.length == 0) {
                                setInputValue("");
                                trySelectionHistory.current = false;
                            }
                            else trySelectionHistory.current = parentTyped == null && inputValue.length == 0;
                            setFocused(false);
                        }}
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                startAdornment: (currentPath.length > 0 || iconStart != undefined || params.InputProps.startAdornment != null)
                                    ? <>
                                        {params.InputProps.startAdornment != null && params.InputProps.startAdornment}

                                        {
                                            (currentPath.length > 0 || iconStart != undefined) &&
                                            <InputAdornment position="start">
                                                {iconStart != null && iconStart}
                                                {currentPath.length > 0 && <Box ml={1}><MprcTypography>{currentPath}</MprcTypography></Box>}
                                            </InputAdornment>
                                        }
                                    </>
                                    : undefined
                                ,
                                endAdornment: (
                                    <>
                                        {loading ? <CircularProgress color="inherit" size={20} /> : undefined}
                                        {params.InputProps.endAdornment}
                                    </>
                                )
                            }
                        }}
                    />
                )}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => {
                    if (option) {
                        const avatarUrl = option.thumbnailUrl == null
                            ? option.thumbnailUrl
                            : option.thumbnailUrl;

                        const avatar = avatarUrl
                            ? <Avatar
                                variant="circular"
                                src={avatarUrl}
                                sx={{ width: 24, height: 24 }}
                            />
                            : undefined;

                        const label = `${onDisplayLabel ? onDisplayLabel(option) : option.label}${option.includeDescendants ? " [+]" : ""}`;

                        // Wrap Tooltip with<></> para remover warning.
                        return (
                            <Tooltip title={option.tooltip ?? ""} key={option.key}>
                                <MprcChip
                                    {...getTagProps({ index })}
                                    key={`chip-${index}`}
                                    label={label}
                                    size="small"
                                    avatar={avatar}
                                    onDelete={!option.canDelete ? undefined : () => handleDeleteChip(option)}
                                />
                            </Tooltip>
                        );
                    }
                    return null;
                })
            }
            slots={{
                paper: ({ children }) => (
                    <Paper elevation={2}>
                        <>
                            {suggestions && suggestions.length > 0 && suggestions[0].fromHistory &&
                                <Typography marginLeft={2} component="div" noWrap={true} variant="caption">{theme.MprcAutoCompleteHierarchical.lastSelectedText}</Typography>
                            }
                            {children}
                        </>
                    </Paper>)
            }}
            onHighlightChange={(event: React.SyntheticEvent, option: any, reason: string) => {
                optionHighlight.current = (reason == "keyboard" || reason == "mouse") && option ? option : null;
            }}
            renderOption={(props, option, ownerState) => {
                if (option.key == KEY_MORE_ITEMS)
                    return <li {...props}><b>{option.label}</b></li>;

                if (option.key == KEY_INCLUDE_DESCENDANTS)
                    return <li {...props}><b style={{ color: muiTheme.palette.highlight.main }}>{option.label}</b></li>;

                if (option.key == KEY_INCLUDE_THIS_ONLY)
                    return <li {...props}><b style={{ color: muiTheme.palette.highlight.main }}>{option.label}</b></li>;

                const matches = match(option.label, ownerState.inputValue);
                const parts = parse(option.label, matches);

                const optionComponent = (
                    <span>
                        {
                            parts.map((part, index) => {
                                return (
                                    <span
                                        key={index}
                                        style={{
                                            fontWeight: part.highlight ? 700 : 400,
                                            whiteSpace: "pre-wrap",
                                        }}
                                    >
                                        {part.text}
                                    </span>
                                );
                            })
                        }
                        {
                            option.includeDescendants &&
                            <span>
                                &nbsp;[+]
                            </span>
                        }
                    </span>
                );

                return (
                    <li {...props} key={option.key}>
                        <Grid container direction="row" justifyContent="center" alignItems="start" spacing={1}>
                            {
                                !option.fromHistory &&
                                <Grid>
                                    <MprcIconFa
                                        icon={option.hasChild ? faSquareArrowDownRight : faSquareDashed}
                                        color="muted"
                                        size="lg"
                                    />
                                </Grid>
                            }
                            <Grid size="grow">
                                {optionComponent}
                            </Grid>
                        </Grid>
                    </li>
                );
            }}
        />
    );
};
